import React, { MouseEvent, forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import useAlertDataForProfiles from '@modules/alerts/hooks/useAlertDataForProfiles'
import { getProfileIsUpdated } from '@modules/alerts/utils'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser, isP1PlusNotFreeUser } from '@modules/auth/types'
import ContactAttendeesPopover from '@modules/chat/components/ContactAttendeesPopover'
import { ChatType } from '@modules/chat/types'
import { useChatContext } from '@modules/chat/useChatContext'
import ContactsHistoryPopover from '@modules/contacts/components/ContactsHistoryPopover'
import useContactWithProfilesCounts from '@modules/contacts/hooks/useContactWithProfilesCounts'
import { Meeting } from '@modules/contacts/types'
import useContactable from '@modules/profile/hooks/useContactable'
import useFetchProfile from '@modules/profile/hooks/useFetchProfile'
import { Delegate, ProfileType } from '@modules/search/types'
import { ShortlistExtendedProfile, ShortlistProfilePriority, ShortlistTableProps } from '@modules/shortlist/types'
import { Box } from '@mui/material'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'

import DraggableTableRow from './DraggableTableRow'
import { EmptyState } from './EmptyState'
import { ShortlistOverviewTableColumnHeaders } from './ShortlistOverviewTableColumnHeaders'
import { TableWrapper } from './index.styles'
import { useTableColumns } from './useTableColumns'

const ShortlistTable = forwardRef<HTMLDivElement, ShortlistTableProps>((props: ShortlistTableProps, ref) => {
  const { t } = useTranslation()
  const {
    activeShortlistId,
    isPageDataLoading = false,
    profilesPageData,
    optionalColumnsConfig,
    selectedRowIds,
    onSelectProfile,
    onClickProfile,
    onEditNote,
    onEditCustomColumn,
    onChangeNda,
    onChangePriority,
    onTagUpdate,
    sort,
    onSortChange,
    alertsShortlistId,
    activeShortlist,
    handleEditCustomColumnSuccess,
    handleCloseEditCustomColumnPopover,
    onEditCustomColumnButtonClick,
    updateConfig,
  } = props

  const [contactStatusDetailsPopoverRef, setContactStatusDetailsPopoverRef] = useState<Nullable<HTMLElement>>(null)
  const [contactStatusDetailsCompanyId, setContactStatusDetailsCompanyId] = useState<Nullable<number>>(null)
  const { openChatModal } = useChatContext()

  const { user } = useAuthContext()
  const isSubscriber = isP1PlusNotFreeUser(user?.subscriptionLevel || 0)
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)
  const { hasAccessToChat, hasAccessToAlerts } = useFeatureFlags()
  const companyContactsMap = useContactWithProfilesCounts({ companyIds: profilesPageData.map(i => i.companyId) })
  const contactableMap = useContactable(profilesPageData.map(profile => Number(profile.companyId)) || [])

  const isContactButtonEnabled = useCallback(
    (companyId: number | null) => {
      if (!contactableMap || companyId == null) return false
      return !!contactableMap[companyId]
    },
    [contactableMap],
  )

  const tableElementRef = useRef<HTMLTableElement>(null)

  const [contactAttendeesPopoverData, setContactAttendeesPopoverData] = useState<
    Nullable<{
      anchor: Nullable<HTMLElement>
      profile: ShortlistExtendedProfile
    }>
  >(null)

  const { data: fetchedProfile } = useFetchProfile({
    id: contactAttendeesPopoverData?.profile.globalId,
    type: contactAttendeesPopoverData?.profile.profileType || ProfileType.delegate,
    disabled: !contactAttendeesPopoverData?.profile,
  })

  const profiles = useMemo(
    () =>
      profilesPageData.map(profilePageData => ({
        id: profilePageData.globalId,
        '@type': profilePageData.profileType,
      })),
    [profilesPageData],
  )

  const { profilesAlertsMap } = useAlertDataForProfiles(profiles, isSubscriber && !!profiles.length)

  const onContactClick = useCallback(
    (e: MouseEvent<HTMLElement>, profile: ShortlistExtendedProfile) => {
      if (profile.profileType === ProfileType.delegate) {
        return openChatModal({
          delegates: [profile.profileImgData as unknown as Delegate],
          companyId: profile.companyId,
          chatType: ChatType.DIRECT,
        })
      }

      if (e?.currentTarget) {
        setContactAttendeesPopoverData({ anchor: e?.currentTarget, profile })
        return null
      }

      return setContactAttendeesPopoverData(null)
    },
    [setContactAttendeesPopoverData, openChatModal],
  )

  const onContactStatusClick = useCallback(
    (popoverRef: Nullable<HTMLElement>, companyId?: number) => {
      setContactStatusDetailsPopoverRef(popoverRef)
      setContactStatusDetailsCompanyId(companyId || null)
    },
    [setContactStatusDetailsPopoverRef, setContactStatusDetailsCompanyId],
  )

  const onNoteClick = useCallback(
    (e: Nullable<MouseEvent<HTMLElement>>, data?: { profileId: number; note: string; profileType: string }) => {
      onEditNote(e, data)
    },
    [onEditNote],
  )

  const onCustomColumnClick = useCallback(
    (e: Nullable<MouseEvent<HTMLElement>>, data?: { profileId: number; fieldId: number; customFieldValue: string }) => {
      onEditCustomColumn(e, data)
    },
    [onEditCustomColumn],
  )

  const pageData = useMemo(
    () =>
      profilesPageData.map(profile => {
        const profileAlert = profilesAlertsMap[profile.globalId]
        return {
          ...profile,
          profileAlert,
          isProfileUpdated: getProfileIsUpdated({
            profile: profile.profileImgData,
            profileAlert,
            type: profile.profileType,
            assetLatestCompany: profile.profileLatestCompany,
            events: profile.profileEvents,
          }),
        }
      }),
    [profilesPageData, profilesAlertsMap],
  )

  const onSelectAllProfiles = useCallback(
    (allSelected: boolean) => {
      onSelectProfile(allSelected ? profilesPageData.map(p => p.profileId) : [])
    },
    [onSelectProfile, profilesPageData],
  )

  const allProfilesSelected = useMemo(() => {
    return profilesPageData.length > 0 && profilesPageData.every(i => selectedRowIds.includes(i.profileId))
  }, [selectedRowIds, profilesPageData])

  const priorityOptions = useMemo(
    () => [
      { label: t(translationKeys.NOT_SET), id: ShortlistProfilePriority.NOT_SET, data: { color: '#E1E1E1' } },
      { label: t(translationKeys.HIGH), id: ShortlistProfilePriority.HIGH, data: { color: '#F27878' } },
      { label: t(translationKeys.MEDIUM), id: ShortlistProfilePriority.MEDIUM, data: { color: '#E9B737' } },
      { label: t(translationKeys.LOW), id: ShortlistProfilePriority.LOW, data: { color: '#8DADFF' } },
    ],
    [t],
  )

  const isContactDetailsPopoverVisible =
    contactStatusDetailsPopoverRef &&
    contactStatusDetailsCompanyId &&
    !!companyContactsMap[contactStatusDetailsCompanyId]?.recentMeeting

  const userAttendingEvents = useMemo(
    () => user?.events.filter(event => event.isAttending).map(event => event.id) ?? [],
    [user?.events],
  )

  const columns = useTableColumns({
    optionalColumnsConfig,
    customFields: activeShortlist?.customFields,
    isEcommerce,
    isSubscriber,
    shortlistId: activeShortlistId,
  })

  const table = useReactTable({
    data: pageData,
    // @ts-expect-error test
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
  })

  useEffect(() => {
    if (table) {
      table.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShortlistId, columns?.length])

  const meta = useMemo(
    () => ({
      companyContactsMap,
      optionalColumnsConfig,
      selectedRowIds,
      onSelectProfile,
      onClickProfile,
      onChangeNda,
      onChangePriority,
      onTagUpdate,
      onNoteClick,
      onCustomColumnClick,
      onContactClick,
      onContactStatusClick,
      alertsShortlistId,
      hasAccessToAlerts,
      hasAccessToChat,
      isEcommerce,
      attendingEvents: userAttendingEvents,
      priorityOptions,
      activeShortlist,
      isContactButtonEnabled,
    }),
    [
      companyContactsMap,
      optionalColumnsConfig,
      selectedRowIds,
      onSelectProfile,
      onClickProfile,
      onChangeNda,
      onChangePriority,
      onTagUpdate,
      onNoteClick,
      onCustomColumnClick,
      onContactClick,
      onContactStatusClick,
      alertsShortlistId,
      hasAccessToAlerts,
      hasAccessToChat,
      isEcommerce,
      userAttendingEvents,
      priorityOptions,
      activeShortlist,
      isContactButtonEnabled,
    ],
  )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tableHeaders = useMemo(() => table.getHeaderGroups()[0].headers.map(header => header), [table, columns.length])

  return (
    <Box sx={{ position: 'relative' }}>
      <Box ref={ref} sx={{ width: '100%', overflowX: 'auto', marginBottom: '-16px' }}>
        <TableWrapper>
          <TableContainer
            component={Paper}
            sx={{
              position: 'relative',
              boxShadow: 'none',
              overflowX: 'inherit',
              paddingBottom: '15px',
            }}
          >
            <Table ref={tableElementRef}>
              {isPageDataLoading ? (
                <EmptyState />
              ) : (
                <>
                  <TableHead>
                    <ShortlistOverviewTableColumnHeaders
                      activeShortlistId={activeShortlistId as number}
                      profiles={profilesPageData}
                      optionalColumnsConfig={optionalColumnsConfig}
                      allProfilesSelected={allProfilesSelected}
                      onSelectAllProfiles={onSelectAllProfiles}
                      sort={sort}
                      onSortChange={onSortChange}
                      // @ts-expect-error TODO: head typing
                      tableColumns={tableHeaders}
                      activeShortlist={activeShortlist}
                      handleEditCustomColumnSuccess={handleEditCustomColumnSuccess}
                      handleCloseEditCustomColumnPopover={handleCloseEditCustomColumnPopover}
                      onEditCustomColumnButtonClick={onEditCustomColumnButtonClick}
                      updateConfig={updateConfig}
                    />
                  </TableHead>
                  <TableBody sx={{ position: 'relative' }}>
                    {table.getRowModel().rows.map(row => (
                      <DraggableTableRow
                        key={`${row.original.profileId}-${columns.length}`}
                        // @ts-expect-error TODO: row typing
                        row={row}
                        optionalColumnsConfig={optionalColumnsConfig}
                        profileData={row.original}
                        highlightedRowLabel={translationKeys.UPDATED}
                        activeShortlist={activeShortlist}
                        meta={meta}
                      />
                    ))}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
          {isContactDetailsPopoverVisible && (
            <ContactsHistoryPopover
              companyId={contactStatusDetailsCompanyId}
              recentMeeting={companyContactsMap[contactStatusDetailsCompanyId].recentMeeting as Meeting}
              anchor={contactStatusDetailsPopoverRef}
              setAnchor={onContactStatusClick}
            />
          )}
          {!!contactAttendeesPopoverData?.anchor && fetchedProfile && (
            <ContactAttendeesPopover
              anchor={contactAttendeesPopoverData?.anchor}
              profile={fetchedProfile}
              close={() => setContactAttendeesPopoverData(null)}
              openChatModal={openChatModal}
            />
          )}
        </TableWrapper>
      </Box>
    </Box>
  )
})

export default memo(ShortlistTable)
